// Owner minutes in the browser (step E62): one ActivityClock per signed-in app, fed the page, whether the tab is
// visible, and the bare fact that a key, click, scroll or touch happened (never which, or where). Approval cards tell
// it when a task is opened, closed and decided (useTaskTimer). Batches go to act record_owner_time: every five minutes
// while there is something to send, soon after a decision, and when the tab is hidden or closed. A failed send is
// dropped rather than retried in a loop. What is and is not measured: lib/ownerTime.ts and docs/COMPLIANCE.md.
import { createContext, useContext, useEffect, useMemo, useRef, type ReactNode } from "react";
import { ActivityClock, DECIDED_FLUSH_MS, FLUSH_EVERY_MS, type TimeEntry } from "./ownerTime";

export interface OwnerTimeApi {
  openTask(id: string): void;
  closeTask(id: string): void;
  decide(id: string): void;
}

const OwnerTimeContext = createContext<OwnerTimeApi | null>(null);

/** The tracker, or null outside the signed-in app (tests, the sign-in page): then nothing is measured. */
export function useOwnerTime(): OwnerTimeApi | null {
  return useContext(OwnerTimeContext);
}

/** Times one task card while it is open; returns a function to call when the task is decided. */
export function useTaskTimer(taskId: string, open: boolean): () => void {
  const api = useOwnerTime();
  useEffect(() => {
    if (!api || !open) return;
    api.openTask(taskId);
    return () => api.closeTask(taskId);
  }, [api, open, taskId]);
  return () => api?.decide(taskId);
}

// Only that something happened: these listeners read nothing from the event.
const INPUT_EVENTS = ["pointerdown", "keydown", "wheel", "scroll", "touchstart", "mousemove"] as const;

export function OwnerTimeProvider({ pathname, send, children }: { pathname: string; send: (entries: TimeEntry[]) => Promise<unknown>; children: ReactNode }) {
  const clockRef = useRef<ActivityClock | null>(null);
  const sendRef = useRef(send);
  sendRef.current = send;
  const pathRef = useRef(pathname);
  pathRef.current = pathname;
  const lastFlush = useRef(Date.now());
  const decidedTimer = useRef<number | null>(null);

  const clock = () => {
    if (!clockRef.current) {
      // the page is set at once: a card's effect (openTask) runs before this provider's own effects
      clockRef.current = new ActivityClock(() => Date.now(), typeof document === "undefined" || document.visibilityState !== "hidden");
      clockRef.current.setPage(pathRef.current);
    }
    return clockRef.current;
  };

  const flush = () => {
    if (decidedTimer.current !== null) {
      window.clearTimeout(decidedTimer.current);
      decidedTimer.current = null;
    }
    lastFlush.current = Date.now();
    const entries = clock().drain();
    if (entries.length === 0) return;
    void sendRef.current(entries).catch(() => {
      /* dropped: owner minutes are a measure, never worth a retry loop or an error on screen */
    });
  };

  useEffect(() => {
    clock().setPage(pathname);
  }, [pathname]);

  useEffect(() => {
    let lastMove = 0;
    const onInput = (e: Event) => {
      // mouse movement is sampled at most every 5 seconds; nothing about the event is kept
      if (e.type === "mousemove") {
        const t = Date.now();
        if (t - lastMove < 5000) return;
        lastMove = t;
      }
      clock().input();
    };
    const onVisibility = () => {
      const visible = document.visibilityState !== "hidden";
      clock().setVisible(visible);
      if (!visible) flush();
    };
    const onHide = () => {
      clock().setVisible(false);
      flush();
    };
    for (const ev of INPUT_EVENTS) window.addEventListener(ev, onInput, { passive: true, capture: true });
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", onHide);
    const every = window.setInterval(() => {
      clock().tick();
      if (Date.now() - lastFlush.current >= FLUSH_EVERY_MS) flush();
    }, 30_000);
    return () => {
      for (const ev of INPUT_EVENTS) window.removeEventListener(ev, onInput, { capture: true });
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", onHide);
      window.clearInterval(every);
      flush();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the listeners are set up once for the page's life
  }, []);

  const api = useMemo<OwnerTimeApi>(
    () => ({
      openTask: (id) => clock().openTask(id),
      closeTask: (id) => clock().closeTask(id),
      decide: (id) => {
        clock().decide(id);
        if (decidedTimer.current === null) decidedTimer.current = window.setTimeout(flush, DECIDED_FLUSH_MS);
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the tracker's methods read refs, so they are made once
    [],
  );

  return <OwnerTimeContext.Provider value={api}>{children}</OwnerTimeContext.Provider>;
}
